import { useEffect, useId } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { useT } from "../i18n/locale";
import type { TabContext } from "../session/tab-context";
import { whatsAppTemplatesQuery } from "./api";
import { unavailableReasonText } from "./whatsapp-texts";

// «WhatsApp» nella testata della scheda (piano M6, R1, Task 4a; ruling A4 e B5; mock-up `scheda-lead.html`): subito dopo «Chiama»,
// sempre in vista. Lo dice la scheda (`whatsapp.available` e `reason`, la stessa regola della preparazione): spento, il perché sta
// sotto i pulsanti come quello di «Chiama». Una scheda che non dice niente del WhatsApp (una risposta di prima del Task 3b: né
// acceso né un motivo) non ha il pulsante. Acceso, la scheda legge subito i modelli, così la finestra (`WhatsAppDialog`, che la
// testata apre fuori dal gruppo delle azioni) si apre già riempita.

export function WhatsAppButton({ tab, card, onOpen }: { tab: TabContext; card: LeadCardResponse; onOpen: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const reasonId = useId();
  const { available, reason } = card.whatsapp;

  useEffect(() => {
    if (available) void queryClient.prefetchQuery(whatsAppTemplatesQuery(tab));
  }, [available, queryClient, tab]);

  if (!available && reason === null) return null;
  const why = !available && reason !== null ? unavailableReasonText(t, card, reason) : null;
  return (
    <>
      <Button aria-haspopup="dialog" disabled={!available} aria-describedby={why ? reasonId : undefined} onClick={onOpen}>
        <Icon name="whatsapp" />
        {t("whatsapp.button")}
      </Button>
      {why ? (
        <Reason tone="warning" id={reasonId} className="g-lead-head__reason">
          {why}
        </Reason>
      ) : null}
    </>
  );
}
